import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useBout } from '../hooks/useBout';
import { BADGES, ROUTES } from '../utils/constants';
import LoadingSpinner from '../components/LoadingSpinner';
import './BoutStandingsPage.css';

export default function BoutStandingsPage() {
  const { boutId } = useParams();
  const { bout, loading } = useBout(boutId);

  if (loading) {
    return (
      <div className="bout-standings-page">
        <LoadingSpinner message="Loading bout..." />
      </div>
    );
  }

  if (!bout) {
    return (
      <div className="bout-standings-page">
        <div className="bout-not-found">
          <h1>Bout Not Found</h1>
          <p>This bout doesn't exist or has been deleted.</p>
          <Link to={ROUTES.ARENA} className="bout-back-link">Back to Arena</Link>
        </div>
      </div>
    );
  }

  const participants = Object.entries(bout.participants || {}).map(([uid, data]) => ({
    uid,
    ...data,
  }));

  // Sort by boutStreak descending, then boutLongestStreak
  participants.sort((a, b) => {
    if (b.boutStreak !== a.boutStreak) return b.boutStreak - a.boutStreak;
    return b.boutLongestStreak - a.boutLongestStreak;
  });

  const visibilityLabel = bout.visibility === 'private' ? 'Private' : 'Public';
  const statusLabel = bout.status === 'active' ? 'Active' : bout.status === 'completed' ? 'Completed' : 'Open';

  return (
    <div className="bout-standings-page">
      <Link to={ROUTES.ARENA} className="bout-back-link">
        &larr; Back to Arena
      </Link>

      <div className="bout-standings-header">
        <h1>
          {visibilityLabel} Bout
        </h1>
        <div className="bout-standings-meta">
          <span className={`bout-standings-status bout-status-${bout.status}`}>
            {statusLabel}
          </span>
          <span className="bout-standings-target">First to {bout.targetStreak}</span>
        </div>
      </div>

      {bout.status === 'completed' && bout.winnerUsername && (
        <div className="bout-winner-banner">
          <span className="bout-winner-trophy">🏆</span>
          <span>Winner: <strong>{bout.winnerUsername}</strong></span>
        </div>
      )}

      <div className="bout-standings-card">
        <table className="bout-standings-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Current Streak</th>
              <th>Best Streak</th>
              <th>Progress</th>
            </tr>
          </thead>
          <tbody>
            {participants.map((p) => {
              const progressPct = Math.min(100, ((p.boutStreak || 0) / bout.targetStreak) * 100);
              const isWinner = bout.winnerUid === p.uid;

              return (
                <tr key={p.uid} className={isWinner ? 'bout-winner-row' : ''}>
                  <td className="bout-player">
                    <span className="bout-player-badge">
                      {BADGES[p.activeBadge]?.emoji || '🐣'}
                    </span>
                    <Link to={`/users/${p.username}`} className="bout-player-username">
                      {p.username}
                    </Link>
                    {isWinner && <span className="bout-winner-icon">🏆</span>}
                  </td>
                  <td className="bout-streak-cell">
                    🔥 {p.boutStreak || 0}
                  </td>
                  <td className="bout-best-cell">
                    {p.boutLongestStreak || 0}
                  </td>
                  <td className="bout-progress-cell">
                    <div className="bout-standings-progress">
                      <div
                        className="bout-standings-progress-fill"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    <span className="bout-standings-progress-text">
                      {p.boutStreak || 0}/{bout.targetStreak}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bout-standings-info">
        <p>
          Created by <Link to={`/users/${bout.createdByUsername}`}>{bout.createdByUsername}</Link>
          {bout.startedAt && (
            <> &middot; Started {bout.startedAt?.toDate?.() ? bout.startedAt.toDate().toLocaleDateString() : ''}</>
          )}
        </p>
      </div>
    </div>
  );
}
